import type {ComponentType} from 'react';

import {CheckboxCellRenderer} from '../components/CheckboxCellRenderer/CheckboxCellRenderer';
import {DateCellRenderer} from '../components/DateCellRenderer/DateCellRenderer';
import {DateTimeCellRenderer} from '../components/DateTimeCellRenderer/DateTimeCellRenderer';
import {FileCellRenderer} from '../components/FileCellRenderer/FileCellRenderer';
import {FilePlainCellRenderer} from '../components/FilePlainCellRenderer/FilePlainCellRenderer';
import {FileSizeCellRenderer} from '../components/FileSizeCellRenderer/FileSizeCellRenderer';
import {FlagCellRenderer} from '../components/FlagCellRenderer/FlagCellRenderer';
import {IconCellRenderer} from '../components/IconCellRenderer/IconCellRenderer';
import {IntegerCellRenderer} from '../components/IntegerCellRenderer/IntegerCellRenderer';
import {LinkCellRenderer} from '../components/LinkCellRenderer/LinkCellRenderer';
import {MetaTextCellRenderer} from '../components/MetaTextCellRenderer/MetaTextCellRenderer';
import {MoneyCellRenderer} from '../components/MoneyCellRenderer/MoneyCellRenderer';
import {MoneyWithoutDecimalCellRenderer} from '../components/MoneyWithoutDecimalCellRenderer/MoneyWithoutDecimalCellRenderer';
import {TagCellRenderer} from '../components/TagCellRenderer/TagCellRenderer';
import {ThumbnailCellRenderer} from '../components/ThumbnailCellRenderer/ThumbnailCellRenderer';
import {TimeIntervalCellRenderer} from '../components/TimeIntervalCellRenderer/TimeIntervalCellRenderer';
import type {CellRendererType} from '../types/EagColumn';

// Individual renderer components declare narrower value types (e.g. number | null),
// so we cannot use ComponentType<CellRendererProps> directly without type errors.
// ComponentType<any> is intentional here: exhaustiveness is instead guaranteed by
// the `Record<Exclude<CellRendererType, 'PLAIN'>, ...>` satisfies clause below.
// eslint-disable-next-line -eslint/no-explicit-any
type AnyRenderer = ComponentType<any>;

// PLAIN is intentionally omitted (Exclude<..., 'PLAIN'>) — it routes to the
// object/label fallback in renderCell.tsx, not to a dedicated renderer component.
// The full (non-Partial) Record over the remaining union members preserves the
// exhaustiveness ts-pattern's .exhaustive() gave: a new CellRendererType added
// without a registry entry is a compile error here.
export const CELL_RENDERER_REGISTRY = {
  MONEY: MoneyCellRenderer,
  MONEY_NO_DECIMAL: MoneyWithoutDecimalCellRenderer,
  DATE: DateCellRenderer,
  DATE_TIME: DateTimeCellRenderer,
  LINK: LinkCellRenderer,
  TAG: TagCellRenderer,
  FILE: FileCellRenderer,
  FILE_PLAIN: FilePlainCellRenderer,
  FLAG: FlagCellRenderer,
  CHECKBOX: CheckboxCellRenderer,
  THUMBNAIL: ThumbnailCellRenderer,
  INTEGER: IntegerCellRenderer,
  FILE_SIZE: FileSizeCellRenderer,
  META_TEXT: MetaTextCellRenderer,
  ICON: IconCellRenderer,
  TIME_INTERVAL: TimeIntervalCellRenderer,
} satisfies Record<Exclude<CellRendererType, 'PLAIN'>, AnyRenderer>;
